import { cn } from "@/lib/utils";
import { CheckCircle, Circle, Edit, FileText, BarChart3, Target } from "lucide-react";

interface WorkflowStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  status: 'completed' | 'current' | 'upcoming';
}

interface WorkflowBarProps {
  currentStep: number;
  steps: WorkflowStep[];
  className?: string;
}

const WorkflowBar = ({ currentStep, steps, className }: WorkflowBarProps) => {
  return (
    <div className={cn("w-full py-6", className)}>
      <div className="flex items-center justify-between relative">
        {/* Progress line */}
        <div className="absolute top-6 left-0 right-0 h-0.5 bg-border">
          <div 
            className="h-full bg-primary transition-all duration-500 ease-in-out"
            style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
          />
        </div>

        {/* Steps */}
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          const isCompleted = step.status === 'completed';
          const isCurrent = step.status === 'current';
          const isUpcoming = step.status === 'upcoming';

          return (
            <div key={step.id} className="flex flex-col items-center relative z-10">
              {/* Step circle */}
              <div
                className={cn(
                  "w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300",
                  "border-2 bg-background",
                  {
                    "border-primary bg-primary text-primary-foreground": isCompleted,
                    "border-primary bg-background text-primary ring-4 ring-primary/20": isCurrent,
                    "border-border bg-background text-muted-foreground": isUpcoming,
                  }
                )}
              >
                {isCompleted ? (
                  <CheckCircle className="w-6 h-6" />
                ) : (
                  <StepIcon className="w-6 h-6" />
                )}
              </div>

              {/* Step content */}
              <div className="mt-3 text-center max-w-24">
                <h3
                  className={cn(
                    "text-sm font-medium transition-colors",
                    {
                      "text-primary": isCompleted || isCurrent,
                      "text-muted-foreground": isUpcoming,
                    }
                  )}
                >
                  {step.title}
                </h3>
                <p
                  className={cn(
                    "text-xs mt-1 transition-colors",
                    {
                      "text-muted-foreground": isCompleted || isCurrent,
                      "text-muted-foreground/60": isUpcoming,
                    }
                  )}
                >
                  {step.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkflowBar;